import { calculateAge } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuGlobe, LuMail, LuMapPin, LuPhone, LuUser } from 'react-icons/lu';

import { PanelSection } from './PanelSection';
import useLead from '~/features/leadDetails/hooks/useLead';
import { formatApplicantAddress } from '~/features/whatsapp/utils/formatApplicantAddress';

type Props = {
    displayName: string;
    phone: string;
    loanRequestId: string | undefined;
};

// Presence/timezone/"Berechtigungen" (Figma) are omitted — no backing field in Meta's Cloud API.
export const ContactSection = ({ displayName, loanRequestId, phone }: Props) => {
    const { t } = useTranslation();
    const { data: lead } = useLead(loanRequestId);
    const personendaten = lead?.antragsteller1?.personendaten;
    const address = formatApplicantAddress(lead?.antragsteller1?.wohnsituation?.anschrift);

    return (
        <>
            {personendaten && (
                <PanelSection title={t('WHATSAPP_CHAT.USER_INFO.CONTACT.ABOUT_TITLE')}>
                    <div className="flex items-center gap-2 py-1.5 text-sm text-midnight">
                        <LuUser className="shrink-0 text-grey" size={16} />
                        <span>
                            {t(`SALUTATIONS.${personendaten.anrede ?? 'DIVERS'}`)}
                            {' • '}
                            {t('AGE_YEARS_ABBREVIATION', {
                                age: calculateAge({ birthdate: personendaten.geburtsdatum }),
                            })}
                            {' • '}
                            {personendaten.staatsangehoerigkeit}
                        </span>
                    </div>
                </PanelSection>
            )}
            <div
                className="flex flex-col gap-1.5 bg-surface-light px-4 py-3 text-sm text-midnight"
                data-testid="user-info-contact-card"
            >
                <div className="flex items-center gap-2 font-medium">
                    <LuUser className="shrink-0 text-grey" size={16} />
                    <span className="truncate">{displayName}</span>
                </div>
                <div className="flex items-center gap-2">
                    <LuPhone className="shrink-0 text-grey" size={16} />
                    <span>{phone}</span>
                </div>
                {personendaten?.email && (
                    <div className="flex items-center gap-2">
                        <LuMail className="shrink-0 text-grey" size={16} />
                        <span className="truncate">{personendaten.email}</span>
                    </div>
                )}
                {address && (
                    <div className="flex items-center gap-2">
                        <LuMapPin className="shrink-0 text-grey" size={16} />
                        <span>{address}</span>
                    </div>
                )}
                {!lead && loanRequestId === undefined && (
                    <div className="flex items-center gap-2 text-sm text-grey">
                        <LuGlobe className="shrink-0" size={14} />
                        <span>{t('WHATSAPP_CHAT.USER_INFO.CONTACT.NO_LINKED_CASE')}</span>
                    </div>
                )}
            </div>
        </>
    );
};
